import { request } from './client'
import type { StaffAuth } from './teaching'
import type { BillingPeriod } from '../types/prisma-enums'

/**
 * F3 — subscriptions.
 *
 * 🔴 PRICES TRAVEL AS INTEGER PAISE AND AS DECIMAL STRINGS, IN THAT ORDER.
 *
 * The server returns `perStudentPaise: 4000`; a form sends `"40.50"`. Neither
 * direction is ever a float — `pricing.ts` on the server explains why at length,
 * and the short version is that `Number('19.99') * 100` is 1998.9999999999998,
 * which loses a paisa per student per month. The only decimal point on this side
 * is in `formatPaise`.
 */

export interface PlanRow {
  id: string
  code: string
  name: string
  perStudentPaise: number
  floorPaise: number
  currency: string
  /**
   * 🔴 THE GENERATED ENUM, NOT A HAND-WRITTEN UNION. This read `'MONTHLY' |
   * 'YEARLY'` — a third copy of the period list, after the service's and the
   * DTO's — and it is the copy that would have silently made a quarterly plan
   * unrenderable. `prisma-enums.ts` is generated from the schema and checked by
   * `types:check`, so there is exactly one list.
   */
  period: BillingPeriod
  isActive: boolean
  isPublic: boolean
  blurb: string | null
}

export interface SubscriptionRow {
  schoolId: string
  schoolName: string
  /** The school's own lifecycle state — what actually decides access. */
  schoolStatus: string
  plan: PlanRow | null
  status: string
  startedAt: string | null
  paidUntil: string | null
  currentPeriodEnd: string | null
  /** Where the period now paid for began — S12 "Period" (P1-18). Null before the first settlement. */
  currentPeriodStart?: string | null
  cancelledAt: string | null
  /** 🔴 Computed from live enrolment, never stored (OD-86). */
  seats: number
  /**
   * 🔴 THE **LIST** PRICE, AND IT KEPT THAT MEANING WHEN DISCOUNTS ARRIVED.
   * Making this the discounted figure would have been the smaller change and the
   * worse one: every existing caller would have silently changed meaning, and a
   * school on a coupon would have had no way to show what the discount was off.
   * `netChargePaise` is what they are actually asked for.
   */
  chargePaise: number | null
  /**
   * The discount in force — the RULE, not the saving.
   *
   * ⚠️ `couponOff` READS AS `20%` OR `500`, UNLOCALISED AND WITHOUT A SYMBOL.
   * Which symbol depends on the plan's currency and Indian digit grouping is
   * 2,2,3 rather than 3,3,3 — both belong to the screen, which is why
   * `formatPaise` exists at the bottom of this file and the server sends neither.
   */
  couponCode: string | null
  couponName: string | null
  couponOff: string | null
  couponPeriodsLeft: number | null
  /** What the coupon takes off at today's seat count. */
  discountPaise: number | null
  /** 🔴 WHAT THE SCHOOL IS ACTUALLY ASKED FOR. */
  netChargePaise: number | null
  /**
   * 🔴 WHEN A FREE TRIAL RUNS OUT. `null` on a `TRIAL` row means *"no end date
   * was set"* — the state every school onboarded before the column existed is
   * in, and one a dashboard must name rather than hide.
   */
  trialEndsAt: string | null
}

/**
 * A discount an operator defined.
 *
 * 🔴 THE RULE, NEVER A COMPUTED SAVING. `off` is `20%` or `500`; what a
 * particular school saves depends on their enrolment and lives on their
 * subscription row. Two figures that look alike and mean different things is how
 * a screen ends up contradicting itself.
 */
export interface CouponRow {
  id: string
  code: string
  name: string
  /* 🔴 `PER_STUDENT` READS `amountOffPaise` AS PAISE PER ENROLLED CHILD. `off`
     is the bare figure for both flat kinds — the screen must add "per child"
     from `kind`, or it prints a price short by the whole enrolment. */
  kind: 'PERCENT' | 'AMOUNT' | 'PER_STUDENT'
  off: string
  percentOff: number | null
  amountOffPaise: number | null
  currency: string
  durationPeriods: number | null
  maxRedemptions: number | null
  redeemedCount: number
  schoolId: string | null
  schoolName: string | null
  newSchoolsOnly: boolean
  startsAt: string | null
  expiresAt: string | null
  isActive: boolean
  createdAt: string
}

/**
 * One claim.
 *
 * ⚠️ THE FIGURES ARE A SNAPSHOT OF THE DAY IT WAS CLAIMED, and the screen must
 * say so. The live saving moves with the roster; this one must not, or *"what did
 * we give them"* is a question whose answer changes every time a child joins.
 */
export interface RedemptionRow {
  id: string
  couponCode: string
  couponName: string
  schoolId: string
  schoolName: string
  grossPaise: number
  discountPaise: number
  currency: string
  seats: number
  createdAt: string
}

export interface TimelineRow {
  id: string
  action: string
  fromStatus: string | null
  toStatus: string | null
  planName: string | null
  seats: number | null
  note: string | null
  effectiveAt: string
}

/**
 * One payment.
 *
 * ⚠️ `method` IS A RAIL, NEVER AN INSTRUMENT — "upi", "card", "netbanking". No
 * last four digits, no VPA, no bank name. The checkout is Razorpay's own hosted
 * page, so an instrument never reaches this origin at all.
 */
export interface PaymentRow {
  id: string
  schoolId: string
  schoolName: string
  amountPaise: number
  currency: string
  status: string
  /** `RAZORPAY` is a signed webhook; `MANUAL` is an operator recording an NEFT. */
  source: string
  method: string | null
  note: string | null
  paidAt: string | null
  createdAt: string
}

export type SubscriptionDetail = SubscriptionRow & { timeline: TimelineRow[] }

/**
 * The school's own read, which carries two things the operator's does not.
 *
 * 🔴 `onlinePaymentAvailable` IS ASKED FOR RATHER THAN ASSUMED. A deployment
 * with no Razorpay credentials is a legitimate configuration — schools pay by
 * NEFT and an operator records it — and D4's rule says a school shown a "Pay
 * now" button that cannot work learns the product is broken. So the server
 * answers, and the screen draws the sentence instead of a dead control.
 */
/**
 * How many places this school has, and how many are in use.
 *
 * 🔴 IT IS NOT `SubscriptionRow.seats`, AND CONFUSING THE TWO WOULD PUT THE
 * WRONG NUMBER ON AN INVOICE. `seats` is live enrolment — the children who
 * exist, which is what the school is billed for. `limit` is the ceiling they
 * signed up for, which only decides whether another child may be added. They
 * are the same number only by coincidence.
 *
 * ⚠️ `null` MEANS UNCAPPED, and every school an operator onboarded by hand is.
 * A screen must render that as "no limit" rather than as zero places.
 */
export interface PlaceUsage {
  used: number
  limit: number | null
  remaining: number | null
}

export type OurSubscription = SubscriptionDetail & {
  payments: PaymentRow[]
  onlinePaymentAvailable: boolean
  places: PlaceUsage
}

export interface CheckoutStart {
  /** Razorpay's hosted page. The browser is sent here. */
  url: string
  amountPaise: number
  currency: string
  /** The date this payment carries the school to, if it succeeds. */
  through: string
  /**
   * 🔴 THE THREE FIGURES TRAVEL TOGETHER, so the last screen before a school's
   * money leaves can show what the discount was OFF rather than only the number
   * after it. `amountPaise` and `grossPaise - discountPaise` are the same value
   * by construction.
   */
  grossPaise: number
  discountPaise: number
  couponCode: string | null
}

/** Public — the published price list. No token. */
export const getPublicPlans = (signal?: AbortSignal): Promise<PlanRow[]> =>
  request<PlanRow[]>('/plans', { context: 'public', signal })

export const getPlans = (auth: StaffAuth = {}): Promise<PlanRow[]> =>
  request<PlanRow[]>('/superadmin/plans', { context: 'user', token: auth.token, signal: auth.signal })

export const createPlan = (
  body: {
    code: string
    name: string
    perStudentPaise: string
    /* Both rates travel as decimal strings — see the header. */
    floorPaise: string
    period?: BillingPeriod
    isPublic?: boolean
    blurb?: string | null
  },
  auth: StaffAuth = {},
): Promise<PlanRow> =>
  request<PlanRow>('/superadmin/plans', {
    method: 'POST',
    body,
    context: 'user',
    token: auth.token,
  })

export const updatePlan = (
  id: string,
  body: Record<string, unknown>,
  auth: StaffAuth = {},
): Promise<PlanRow> =>
  request<PlanRow>(`/superadmin/plans/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    context: 'user',
    token: auth.token,
  })

export const getSubscriptions = (auth: StaffAuth = {}): Promise<SubscriptionRow[]> =>
  request<SubscriptionRow[]>('/superadmin/subscriptions', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

export const getSubscription = (
  schoolId: string,
  auth: StaffAuth = {},
): Promise<SubscriptionDetail> =>
  request<SubscriptionDetail>(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/subscription`,
    { context: 'user', token: auth.token, signal: auth.signal },
  )

export const startSubscription = (
  schoolId: string,
  body: { planId: string; status?: string; currentPeriodEnd?: string | null; note?: string | null },
  auth: StaffAuth = {},
): Promise<SubscriptionRow> =>
  request<SubscriptionRow>(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/subscription`,
    { method: 'POST', body, context: 'user', token: auth.token },
  )

/**
 * 🔴 THE ROUTE THAT EXISTED ON THE SERVER AND HAD NO CLIENT.
 *
 * `POST /superadmin/schools/:id/subscription/trial` has shipped since F3 and
 * nothing in this app could reach it, so the only way a school ever got a trial
 * with an end date was to sign itself up. Every trial an operator granted was
 * the open-ended kind `billing.dto.ts` calls *"a free customer nobody decided to
 * give away"*.
 *
 * ⚠️ `trialEndsAt` IS AN ABSOLUTE DATE, NOT A NUMBER OF DAYS, and the screen is
 * what does the arithmetic. The DTO's argument: *"'30 days' has to be resolved
 * against a clock somewhere, and doing it on the server means the operator is
 * agreeing to a date they cannot see."*
 *
 * ⚠️ OMIT `planId` TO EXTEND A TRIAL ON THE PLAN THEY ARE ALREADY ON. Sending
 * one read off a dropdown that defaulted to its first option is how extending a
 * trial silently moves a school onto a different plan.
 *
 * ⚠️ AND OMIT `seatLimit` TO LEAVE THE CEILING WHERE IT IS. Sending `null` does
 * not clear it — the route has no way to clear it, deliberately — but sending a
 * number read off an empty field would set one nobody chose.
 */
export const grantTrial = (
  schoolId: string,
  body: {
    trialEndsAt: string
    planId?: string
    seatLimit?: number
    note?: string | null
  },
  auth: StaffAuth = {},
): Promise<SubscriptionRow> =>
  request<SubscriptionRow>(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/subscription/trial`,
    { method: 'POST', body, context: 'user', token: auth.token },
  )

export const recordSubscriptionStatus = (
  schoolId: string,
  body: {
    status: string
    paidUntil?: string | null
    currentPeriodEnd?: string | null
    note?: string | null
  },
  auth: StaffAuth = {},
): Promise<SubscriptionRow> =>
  request<SubscriptionRow>(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/subscription/status`,
    { method: 'POST', body, context: 'user', token: auth.token },
  )

/* ───────────────────────────────────────────────────────── coupons ── */

export const getCoupons = (auth: StaffAuth = {}): Promise<CouponRow[]> =>
  request<CouponRow[]>('/superadmin/coupons', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * 🔴 EXACTLY ONE OF `percentOff` AND `amountOffPaise`. The server refuses a body
 * carrying both or neither — `discount.ts` treats a malformed pair as NO
 * discount, which is the right failure for arithmetic that must never hand money
 * away, and would make a coupon an operator believes in do nothing at all.
 *
 * ⚠️ THE FLAT AMOUNT TRAVELS AS A DECIMAL STRING, like every other figure in
 * this file. A number on the wire is a float, and a float is what money must not
 * be.
 */
export const createCoupon = (
  body: {
    code: string
    name: string
    /* 🔴 EXACTLY ONE OF THE THREE. The server refuses zero or two of them, and
       the two flat ones differ by a factor of the school's whole enrolment. */
    percentOff?: number
    amountOffPaise?: string
    perStudentOffPaise?: string
    currency?: string
    durationPeriods?: number | null
    maxRedemptions?: number | null
    schoolId?: string | null
    newSchoolsOnly?: boolean
    startsAt?: string | null
    expiresAt?: string | null
  },
  auth: StaffAuth = {},
): Promise<CouponRow> =>
  request<CouponRow>('/superadmin/coupons', {
    method: 'POST',
    body,
    context: 'user',
    token: auth.token,
  })

/**
 * ⚠️ NEITHER THE CODE NOR THE DISCOUNT ITSELF CAN BE CHANGED, and the server
 * enforces it. The code is sitting in somebody's inbox; re-pricing a coupon
 * would silently re-price every school already on it. What moves is the window,
 * the caps and whether it is still claimable.
 */
export const updateCoupon = (
  id: string,
  body: Record<string, unknown>,
  auth: StaffAuth = {},
): Promise<CouponRow> =>
  request<CouponRow>(`/superadmin/coupons/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    context: 'user',
    token: auth.token,
  })

export const getRedemptions = (auth: StaffAuth = {}): Promise<RedemptionRow[]> =>
  request<RedemptionRow[]>('/superadmin/coupon-redemptions', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** Operator — put a named school on a discount. Refusals here are specific. */
export const applyCouponToSchool = (
  schoolId: string,
  code: string,
  auth: StaffAuth = {},
): Promise<SubscriptionDetail> =>
  request<SubscriptionDetail>(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/subscription/coupon`,
    { method: 'POST', body: { code }, context: 'user', token: auth.token },
  )

/**
 * Operator — take a discount off.
 *
 * ⚠️ THE REDEMPTION ROW STAYS. It is the record that the discount WAS claimed,
 * and deleting it would free a slot against the coupon's cap that a school
 * genuinely used.
 */
export const removeCouponFromSchool = (
  schoolId: string,
  auth: StaffAuth = {},
): Promise<SubscriptionDetail> =>
  request<SubscriptionDetail>(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/subscription/coupon`,
    { method: 'DELETE', context: 'user', token: auth.token },
  )

/**
 * A school entering a code they were given.
 *
 * 🔴 THE ONLY WRITE A SCHOOL MAY MAKE TO ITS OWN ARRANGEMENT, AND IT IS NOT THE
 * EXCEPTION IT LOOKS LIKE. They do not choose the discount — they name a rule an
 * operator already wrote down, and the server decides whether it applies to them
 * and what it is worth. A school still cannot pick a plan, change a seat count or
 * influence an amount.
 *
 * ⚠️ EXPECT ONE GENERIC REFUSAL FOR EVERY FAILURE, and do not try to improve on
 * it. Unknown, expired, exhausted, bound to another school and "you have already
 * paid" all come back the same, because a code is typed off an email and telling
 * them apart would make this an oracle over which codes are real.
 */
/*
 * ⚠️ IT RETURNS `SubscriptionDetail`, NOT `OurSubscription`. The route answers
 * with the same shape the operator's detail route does — payments, places and
 * `onlinePaymentAvailable` are assembled by `GET /school/subscription` and are
 * not re-read here. The screen refetches; typing this as the wider shape would
 * be a promise the server does not keep.
 */
export const applyOurCoupon = (code: string, auth: StaffAuth = {}): Promise<SubscriptionDetail> =>
  request<SubscriptionDetail>('/school/subscription/coupon', {
    method: 'POST',
    body: { code },
    context: 'user',
    token: auth.token,
  })

/** The school's own read. No `:schoolId` — it comes from the token (§8.1). */
export const getOurSubscription = (auth: StaffAuth = {}): Promise<OurSubscription> =>
  request<OurSubscription>('/school/subscription', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** Money that arrived, across every school. Operator only. */
export const getPayments = (auth: StaffAuth = {}): Promise<PaymentRow[]> =>
  request<PaymentRow[]>('/superadmin/payments', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * Raise a payment link for this school's current period.
 *
 * 🔴 IT SENDS NO BODY AT ALL, AND THAT IS THE SECURITY PROPERTY RATHER THAN A
 * SAVING. The amount, the plan and the period are all computed on the server
 * from the school's own subscription and its live enrolment. An amount in this
 * request — which the screen already knows and could easily send — is the one
 * shape that turns a checkout into a discount form.
 *
 * ⚠️ THE RETURNED URL IS RAZORPAY'S HOSTED PAGE, and the browser is sent there.
 * No checkout script is loaded on this origin, so a card number never touches
 * it. Payment is confirmed by the signed webhook; the browser coming back
 * afterwards decides which screen to show and nothing else.
 */
export const startCheckout = (auth: StaffAuth = {}): Promise<CheckoutStart> =>
  request<CheckoutStart>('/school/subscription/checkout', {
    method: 'POST',
    context: 'user',
    token: auth.token,
  })

/**
 * Paise → what a human reads.
 *
 * 🔴 INDIAN DIGIT GROUPING IS 2,2,3 — NOT 3,3,3. `Intl` with the wrong locale
 * renders ₹12,34,567 as ₹1,234,567, which a school reads as ten times the price.
 * `en-IN` is passed explicitly for that reason, and the currency comes from the
 * plan rather than being assumed.
 */
export function formatPaise(paise: number, currency = 'INR'): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(paise / 100)
}
